/* eslint-disable no-plusplus */

import {
    Button,
    Flex,
    NumberInput,
    Select,
    SelectItem,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeaderCell,
    TableRow,
    Text,
    TextInput,
    Title,
} from '@tremor/react'
import { useEffect, useState } from 'react'
import { useAuth } from '../../utilities/auth'
import { ModelOS, ModelVMType } from '../../api/api'
import api from '../../api/ApiConfig'
import { generateVMs } from '../pricing'
import {
    useApiV1KeypairsList,
    useApiV1UsersList,
    useApiV1VmCreateCreate,
    useApiV1VmOsList,
    useApiV1VmsList,
    useApiV1BillingTransactionsList,
    useApiV1VmCreateBatchCreate,
} from '../../api/api.gen'
import Modal from '../../components/Layout/modal'

interface AdminUser {
    id?: string
    email?: string
    balance?: number
}

export default function AdminPage() {
    const { user } = useAuth()
    const [selectedUser, setSelectedUser] = useState<AdminUser>()
    const [showVMsModal, setShowVMsModal] = useState(false)
    const [showTransactionsModal, setShowTransactionsModal] = useState(false)
    const [showCreateModal, setShowCreateModal] = useState(false)
    const [showPlanModal, setShowPlanModal] = useState(false)

    const [planCount, setPlanCount] = useState(1)
    const [planBudget, setPlanBudget] = useState(0)
    const {
        isLoading: createBatchLoading,
        isExecuted: createBatchExecuted,
        sendNowWithParams: callCreateBatch,
    } = useApiV1VmCreateBatchCreate({}, {}, {}, false)

    const generateVMNameSizes = (count: number) => {
        const names: { name: string; size: number }[] = []
        const fixed = ['pg-master', 'pg-slave', 'mysql-master', 'mysql-slave']
        fixed.forEach((n) => {
            if (names.length < count) names.push({ name: n, size: 100 })
        })
        let es = 1
        let k8s = 1
        let vm = 1
        while (names.length < count) {
            if (names.length < count)
                names.push({ name: `elasticsearch-${es++}`, size: 300 })
            if (names.length < count)
                names.push({ name: `k8s-wk-${k8s++}`, size: 30 })
            if (names.length < count)
                names.push({ name: `vm-${vm++}`, size: 80 })
        }
        return names
    }

    const suggestPlan = (count: number, budget: number) => {
        const options = generateVMs()
            .filter((v) => v.priceOnDemand)
            .map((v) => ({
                ...v,
                monthlyCost: (v.priceOnDemand || 0) * 720,
                description: `${v.cpu} cores, ${v.ram} GB${
                    v.shared ? ' (shared)' : ''
                }${v.gpu ? ', GPU' : ''}`,
            }))
            .sort((a, b) => a.monthlyCost - b.monthlyCost)

        let remainingBudget = budget
        let remainingCount = count
        const result: {
            id: ModelVMType
            name: string
            volumeSize: number
            description: string
            count: number
            monthlyCost: number
        }[] = []

        const names = generateVMNameSizes(count)
        let nameIdx = 0
        while (remainingCount > 0) {
            const avg = remainingBudget / remainingCount
            const choice = options
                .slice()
                .reverse()
                .find((v) => v.monthlyCost <= avg)

            if (!choice) {
                break
            }

            // const existing = result.find(
            //     (r) => r.description === choice.description
            // )
            // if (existing) {
            //     existing.count += 1
            // } else {
            result.push({
                id: choice.id,
                name: names[nameIdx].name,
                volumeSize: names[nameIdx].size,
                description: choice.description,
                count: 1,
                monthlyCost: choice.monthlyCost + names[nameIdx].size * 0.06351,
            })
            // }

            remainingBudget -=
                choice.monthlyCost + names[nameIdx].size * 0.06351
            remainingCount -= 1
            nameIdx++
        }

        return remainingCount === 0 ? result : undefined
    }

    const suggestedPlan =
        planBudget > 0 && planCount > 0
            ? suggestPlan(planCount, planBudget)
            : undefined

    const createBatch = async () => {
        /* eslint-disable prefer-template */
        const keyPairName = (selectedUser?.id?.slice(0, 4) || '') + '-admin'
        const vms = suggestedPlan?.map((n) => ({
            keyPairName,
            os: ModelOS.OSUbuntu,
            vmType: n.id,
            name: n.name,
            volumeSize: n.volumeSize,
        }))
        callCreateBatch(
            {
                vms,
            },
            {
                user: selectedUser?.id,
            },
            {}
        )
    }

    useEffect(() => {
        if (createBatchExecuted && !createBatchLoading) {
            setShowCreateModal(false)
        }
    }, [createBatchLoading])

    const [vmName, setVMName] = useState('')
    const [keyPairName, setKeyPairName] = useState('')
    const [ami, setAMI] = useState('')
    const [vmType, setVMType] = useState<ModelVMType>(
        ModelVMType.VMTypeBurstable2Core4GB
    )
    const [volumeSize, setVolumeSize] = useState(8)

    const { response: keypairList } = useApiV1KeypairsList()
    const { response: osList } = useApiV1VmOsList({ filter: '' })
    const { response: users, isLoading: usersLoading } = useApiV1UsersList()
    const sortedUsers = (users || [])
        .slice()
        .sort((a, b) => (b.balance || 0) - (a.balance || 0))
    const { response: vms, isLoading: vmsLoading } = useApiV1VmsList(
        { user: selectedUser?.id },
        {},
        showVMsModal
    )
    const { response: transactions, isLoading: transactionsLoading } =
        useApiV1BillingTransactionsList(
            { user: selectedUser?.id },
            {},
            showTransactionsModal
        )
    const {
        isLoading: createLoading,
        isExecuted: createExecuted,
        response: createResponse,
        sendNowWithParams: create,
        error: createError,
    } = useApiV1VmCreateCreate({}, {}, {}, false)

    if (
        user.email !== 'saleh.khazaei@gmail.com' &&
        user.email !== 'h.abolhelm@gmail.com'
    ) {
        return (
            <Flex
                className="p-4"
                flexDirection="col"
                justifyContent="start"
                alignItems="start"
            >
                <Title className="text-teal-600">Administrator</Title>
                <Text>Unauthorized</Text>
            </Flex>
        )
    }

    return (
        <Flex
            className="p-4 bg-gray-50 w-auto min-w-[1000px] max-w-fit mt-4 rounded-md"
            flexDirection="col"
            alignItems="start"
        >
            <Title className="text-teal-600 mb-4">Users</Title>
            <Table className="w-full">
                <TableHead>
                    <TableRow>
                        <TableHeaderCell>Email</TableHeaderCell>
                        <TableHeaderCell>Balance</TableHeaderCell>
                        <TableHeaderCell>Actions</TableHeaderCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sortedUsers.map((u) => (
                        <TableRow key={u.id}>
                            <TableCell>{u.id}</TableCell>
                            <TableCell>{u.balance?.toFixed(3)}</TableCell>
                            <TableCell>
                                <Flex className="space-x-2">
                                    <Button
                                        size="xs"
                                        variant="secondary"
                                        color="teal"
                                        onClick={() => {
                                            setSelectedUser(u)
                                            setShowVMsModal(true)
                                        }}
                                    >
                                        View VMs
                                    </Button>
                                    <Button
                                        size="xs"
                                        variant="secondary"
                                        color="teal"
                                        onClick={() => {
                                            setSelectedUser(u)
                                            setShowTransactionsModal(true)
                                        }}
                                    >
                                        Transactions
                                    </Button>
                                    <Button
                                        size="xs"
                                        variant="secondary"
                                        color="teal"
                                        onClick={() => {
                                            setSelectedUser(u)
                                            setShowPlanModal(true)
                                        }}
                                    >
                                        Plan VMs
                                    </Button>
                                </Flex>
                            </TableCell>
                        </TableRow>
                    ))}
                    {sortedUsers.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={3}>
                                <Text className="w-full text-gray-400 text-center font-normal">
                                    {usersLoading ? 'Loading' : 'Empty list'}
                                </Text>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>

            <Modal
                title={`${selectedUser?.email || ''}'s VMs`}
                show={showVMsModal}
                onCloseClicked={() => {
                    setShowVMsModal(false)
                    setSelectedUser(undefined)
                }}
                className="min-w-[600px]"
                position="center"
            >
                <Flex
                    flexDirection="row"
                    justifyContent="between"
                    className="mb-2 w-full"
                >
                    <div />
                    <Button
                        variant="secondary"
                        color="teal"
                        onClick={() => setShowCreateModal(true)}
                    >
                        Create VM
                    </Button>
                </Flex>
                <Table className="w-full">
                    <TableHead>
                        <TableRow>
                            <TableHeaderCell>Name</TableHeaderCell>
                            <TableHeaderCell>Status</TableHeaderCell>
                            <TableHeaderCell>IP</TableHeaderCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {vms?.items?.map((vm) => (
                            <TableRow key={vm.id}>
                                <TableCell>{vm.name}</TableCell>
                                <TableCell>{vm.status}</TableCell>
                                <TableCell>{vm.ipAddress}</TableCell>
                            </TableRow>
                        ))}
                        {vms?.items?.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={3}>
                                    <Text className="w-full text-gray-400 text-center font-normal">
                                        {vmsLoading ? 'Loading' : 'Empty list'}
                                    </Text>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Modal>

            <Modal
                title={`${selectedUser?.email || ''}'s Transactions`}
                show={showTransactionsModal}
                onCloseClicked={() => {
                    setShowTransactionsModal(false)
                    setSelectedUser(undefined)
                }}
                className="min-w-[600px]"
                position="center"
            >
                <Table className="w-full">
                    <TableHead>
                        <TableRow>
                            <TableHeaderCell>Amount</TableHeaderCell>
                            <TableHeaderCell>Product</TableHeaderCell>
                            <TableHeaderCell>Date</TableHeaderCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {transactions?.transactions?.map((t) => (
                            <TableRow key={t.id}>
                                <TableCell>{t.amount}</TableCell>
                                <TableCell>{t.productType}</TableCell>
                                <TableCell>
                                    {t.createdAt
                                        ? new Date(t.createdAt).toLocaleString()
                                        : ''}
                                </TableCell>
                            </TableRow>
                        ))}
                        {transactions?.transactions?.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={3}>
                                    <Text className="w-full text-gray-400 text-center font-normal">
                                        {transactionsLoading
                                            ? 'Loading'
                                            : 'Empty list'}
                                    </Text>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Modal>

            <Modal
                title={`Plan VMs for ${selectedUser?.email || ''}`}
                show={showPlanModal}
                onCloseClicked={() => {
                    setShowPlanModal(false)
                    setSelectedUser(undefined)
                }}
                position="center"
            >
                <Text className="mt-4 mb-1">Number of VMs</Text>
                <NumberInput value={planCount} onValueChange={setPlanCount} />
                <Text className="mt-4 mb-1">Monthly Budget (OMR)</Text>
                <NumberInput value={planBudget} onValueChange={setPlanBudget} />
                {planBudget > 0 &&
                    planCount > 0 &&
                    (suggestedPlan ? (
                        <>
                            <Text className="mt-4">Recommended VMs:</Text>
                            {suggestedPlan.map((p) => (
                                <Text className="mt-4">
                                    - [{p.name}]: {p.count} x {p.description} +{' '}
                                    {p.volumeSize}GB (
                                    {(p.count * p.monthlyCost).toFixed(3)}{' '}
                                    OMR/month)
                                </Text>
                            ))}

                            <Text className="mt-4">
                                Total estimated cost:{' '}
                                {suggestedPlan
                                    .reduce(
                                        (sum, p) =>
                                            sum + p.count * p.monthlyCost,
                                        0
                                    )
                                    .toFixed(3)}
                                OMR/month
                            </Text>
                        </>
                    ) : (
                        <Text className="mt-4">
                            Budget too low for available VMs
                        </Text>
                    ))}
                <Button
                    className="mt-4"
                    variant="secondary"
                    color="teal"
                    onClick={createBatch}
                    disabled={planCount <= 0}
                >
                    Create
                </Button>
            </Modal>

            <Modal
                title="Create VM"
                show={showCreateModal}
                onCloseClicked={() => setShowCreateModal(false)}
                position="center"
            >
                <Text className="mt-4 mb-1">Name</Text>
                <TextInput value={vmName} onValueChange={setVMName} />

                <Text className="mt-4 mb-1">Key Pair</Text>
                <Select value={keyPairName} onValueChange={setKeyPairName}>
                    {keypairList?.items?.map((k) => (
                        <SelectItem key={k.name} value={k.name || ''}>
                            {k.name}
                        </SelectItem>
                    ))}
                </Select>

                <Text className="mt-4 mb-1">OS</Text>
                <Select value={ami} onValueChange={setAMI}>
                    {osList?.items?.map((o) => (
                        <SelectItem key={o.id} value={o.id || ''}>
                            {o.name || o.description}
                        </SelectItem>
                    ))}
                </Select>

                <Text className="mt-4 mb-1">VM Type</Text>
                <Select
                    value={vmType}
                    onValueChange={(v) => setVMType(v as ModelVMType)}
                >
                    {Object.values(ModelVMType).map((t) => (
                        <SelectItem key={t} value={t}>
                            {t}
                        </SelectItem>
                    ))}
                </Select>

                <Text className="mt-4 mb-1">Volume Size</Text>
                <NumberInput value={volumeSize} onValueChange={setVolumeSize} />

                <Button
                    className="mt-4"
                    variant="secondary"
                    color="teal"
                    onClick={() =>
                        create(
                            {
                                name: vmName,
                                keyPairName,
                                osami: ami,
                                vmType,
                                volumeSize,
                            },
                            { user: selectedUser?.id },
                            {}
                        )
                    }
                    disabled={!vmName || !keyPairName || !ami}
                >
                    Create
                </Button>
            </Modal>
        </Flex>
    )
}
